import { ChangeEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { t } from 'i18next'
import { ArrayElement } from 'mongodb'

import {
  AppSegment,
  AppLoader,
  AppFlexContainer,
  AppFlexItem,
  AppHeader,
  AppParagraph,
  AppFormSelect,
  AppDivider,
} from 'client/ui-building-blocks'

import { UserAvatar } from 'client/components'
import { useGetTopUsers } from 'client/api'
import type { TGetTopUsersData } from 'client/api/useGetTopUsers/useGetTopUsers'

type UserStat = ArrayElement<TGetTopUsersData['data']>

type RowProps = {
  _id: string
  username: string
  secondaryText: string
  renderBorder: boolean
}

enum SelectOption {
  MOST_CORRECT_ANSWERS = 'most_correct_answers',
  TOP_CORRECTNESS = 'top_correctness',
  MOST_ANSWERS = 'most_answers',
}

const OPTIONS = [
  {
    value: SelectOption.MOST_CORRECT_ANSWERS,
    text: t('bestOfTheBestPage.options', { returnObjects: true })[0],
  },
  {
    value: SelectOption.TOP_CORRECTNESS,
    text: t('bestOfTheBestPage.options', { returnObjects: true })[1],
  },
  {
    value: SelectOption.MOST_ANSWERS,
    text: t('bestOfTheBestPage.options', { returnObjects: true })[2],
  },
]

const SORT_METHODS = {
  [SelectOption.MOST_CORRECT_ANSWERS]: (a: UserStat, b: UserStat) =>
    b.answeredCorrectCount - a.answeredCorrectCount,
  [SelectOption.TOP_CORRECTNESS]: (a: UserStat, b: UserStat) =>
    b.answeredCorrectCount / b.answeredCount - a.answeredCorrectCount / a.answeredCount,
  [SelectOption.MOST_ANSWERS]: (a: UserStat, b: UserStat) =>
    b.answeredCount - a.answeredCount,
}

const FILTER_METHODS = {
  [SelectOption.MOST_CORRECT_ANSWERS]: (item: UserStat) => item.answeredCorrectCount,
  [SelectOption.TOP_CORRECTNESS]: (item: UserStat) => item.answeredCorrectCount,
  [SelectOption.MOST_ANSWERS]: (item: UserStat) => item.answeredCount,
}

const GET_SECONDARY_TEXT_METHODS = {
  [SelectOption.MOST_CORRECT_ANSWERS]: (item: UserStat) =>
    t('bestOfTheBestPage.correctAnswers', { count: item.answeredCorrectCount }),
  [SelectOption.TOP_CORRECTNESS]: (item: UserStat) =>
    t('bestOfTheBestPage.correctness', {
      percentage: `${((item.answeredCorrectCount / item.answeredCount || 0) * 100).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`,
    }),
  [SelectOption.MOST_ANSWERS]: (item: UserStat, questionsCount: number) =>
    t('bestOfTheBestPage.allAnswers', {
      count: item.answeredCount,
      percentage: `${((item.answeredCount / questionsCount || 0) * 100).toLocaleString('en', { minimumFractionDigits: 0, maximumFractionDigits: 1 })}%`,
    }),
}

const INFO_TEXT = {
  [SelectOption.MOST_CORRECT_ANSWERS]: t('bestOfTheBestPage.info', {
    returnObjects: true,
  })[0],
  [SelectOption.TOP_CORRECTNESS]: t('bestOfTheBestPage.info', { returnObjects: true })[1],
  [SelectOption.MOST_ANSWERS]: t('bestOfTheBestPage.info', { returnObjects: true })[2],
}

const Row = ({ _id, username, secondaryText, renderBorder }: RowProps) => {
  return (
    <AppFlexItem width="100%">
      <AppFlexContainer alignItems="center" columnGap="20px">
        <AppFlexItem>
          <UserAvatar userId={_id} size="small" profileLink="NAVIGATE" />
        </AppFlexItem>
        <AppFlexItem>
          <AppHeader text={username} />
          <AppParagraph text={secondaryText} color="secondary" />
        </AppFlexItem>
      </AppFlexContainer>
      {renderBorder ? <AppDivider border="weak" size="small" /> : null}
    </AppFlexItem>
  )
}

const BestOfTheBestPage = () => {
  const { t } = useTranslation()

  const [selectValue, setSelectValue] = useState<SelectOption>(
    SelectOption.MOST_CORRECT_ANSWERS
  )

  const { data, isLoading, isFetching } = useGetTopUsers('BEST')

  const onSelectChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSelectValue(e.target.value as SelectOption)
  }, [])

  const finalData =
    data?.data.filter(FILTER_METHODS[selectValue]).sort(SORT_METHODS[selectValue]) || []

  return (
    <AppSegment
      headerProps={{
        text: t('bestOfTheBestPage.pageHeaderText'),
        iconProps: {
          name: 'CHART_BAR',
        },
        marginBottom: 'xsmall',
      }}
      maxWidth="medium"
      centered
    >
      <AppLoader active={isLoading || isFetching}>
        <AppFlexContainer verticalOnMobile>
          <AppFlexItem minWidth="220px">
            <AppFormSelect
              options={OPTIONS}
              value={selectValue}
              onChange={onSelectChange}
              disabled={!data?.data.length}
            />
          </AppFlexItem>
        </AppFlexContainer>
        {data?.data.length ? (
          <>
            <AppDivider />
            <AppParagraph text={INFO_TEXT[selectValue]} marginBottom="medium" />
            <AppFlexContainer flexFlow="column" rowGap="20px">
              {finalData.map((item, i) => (
                <Row
                  key={i}
                  {...item}
                  secondaryText={GET_SECONDARY_TEXT_METHODS[selectValue](
                    item,
                    data?.questionsCount
                  )}
                  renderBorder={i < finalData.length - 1}
                />
              ))}
            </AppFlexContainer>
          </>
        ) : null}
      </AppLoader>
    </AppSegment>
  )
}

export default BestOfTheBestPage
